import React, { useState } from "react";
import { ICooperativeDetails } from "../../exports/interfaces";
import { Info } from "./CooperativeDetails";
import useMobile from "../../hooks/useMobile";
import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";

const FinancialInfo = ({
  cooperative,
}: {
  cooperative: ICooperativeDetails | null;
}) => {
  const [financialsactiveTab, setFinancialsactiveTab] = useState("share");
  const isMobile = useMobile();
  const { canEdit } = usePermission();
  const navigate = useNavigate();
  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="primary-color">Financial Information</h6>
        {canEdit("cooperatives") && (
          <button
            className="btn px-3 btn-badge fs-small"
            onClick={() =>
              navigate(`/register/edit/${cooperative?.id}`, {
                state: {
                  step: 2,
                  tab:
                    financialsactiveTab === "capital"
                      ? "loan"
                      : financialsactiveTab,
                },
              })
            }
          >
            <i className={`${!isMobile ? "me-2" : ""}`}>
              <Pencil size={15} />
            </i>
            {!isMobile && "Edit"}
          </button>
        )}
      </div>
      <div className="row g-3">
        <div className="col-md-12 details-text">
          <small className="d-block">Fiscal Year</small>
          <p>
            {financialsactiveTab === "share"
              ? cooperative?.financial?.share?.fiscalYear
              : financialsactiveTab === "capital"
                ? cooperative?.financial?.loan?.fiscalYear
                : financialsactiveTab === "income"
                  ? cooperative?.financial?.income?.fiscalYear
                  : financialsactiveTab === "audit"
                    ? cooperative?.financial?.audit?.fiscalYear
                    : financialsactiveTab === "tax"
                      ? cooperative?.financial?.tax?.fiscalYear
                      : "-"}
          </p>
        </div>
        <div className="col-md-12 details-text">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-badge-border px-3"
              onClick={() => setFinancialsactiveTab("share")}
              style={
                financialsactiveTab === "share" ? { fontWeight: "bold" } : {}
              }
            >
              Share
            </button>

            <button
              className="btn btn-badge-border px-3"
              onClick={() => setFinancialsactiveTab("capital")}
              style={
                financialsactiveTab === "capital" ? { fontWeight: "bold" } : {}
              }
            >
              Capital and Loan
            </button>
            <button
              className="btn btn-badge-border px-3"
              onClick={() => setFinancialsactiveTab("income")}
              style={
                financialsactiveTab === "income" ? { fontWeight: "bold" } : {}
              }
            >
              Income and Expenses
            </button>
            <button
              className="btn btn-badge-border px-3"
              onClick={() => setFinancialsactiveTab("audit")}
              style={
                financialsactiveTab === "audit" ? { fontWeight: "bold" } : {}
              }
            >
              Audit
            </button>

            <button
              className="btn btn-badge-border px-3"
              onClick={() => setFinancialsactiveTab("tax")}
              style={
                financialsactiveTab === "tax" ? { fontWeight: "bold" } : {}
              }
            >
              Tax
            </button>
          </div>
          {financialsactiveTab === "share" && (
            <div className="dashboard-card mt-3">
              <h6 className="text-uppercase mb-3">
                Membership & Share Information
              </h6>

              <div className="row g-4">
                <Info
                  label="Male Members"
                  value={cooperative?.financial?.share.maleMembers ?? "-"}
                />

                <Info
                  label="Female Members"
                  value={cooperative?.financial?.share.femaleMembers ?? "-"}
                />

                <Info
                  label="Other Members"
                  value={cooperative?.financial?.share.otherMembers ?? "-"}
                />

                <Info
                  label="Institutional Members"
                  value={
                    cooperative?.financial?.share.institutionalMembers ?? "-"
                  }
                />

                <Info
                  label="Total Share Members"
                  value={cooperative?.financial?.share.totalShareMembers ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Savings Information</h6>

              <div className="row g-4">
                <Info
                  label="General Saving"
                  value={
                    cooperative?.financial?.share.totalGeneralSaving ?? "-"
                  }
                />

                <Info
                  label="Regular Saving"
                  value={
                    cooperative?.financial?.share.totalRegularSaving ?? "-"
                  }
                />

                <Info
                  label="Periodic Saving"
                  value={
                    cooperative?.financial?.share.totalPeriodicSaving ?? "-"
                  }
                />

                <Info
                  label="Saving Amount"
                  value={cooperative?.financial?.share.totalSavingAmount ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Assets & Liabilities</h6>

              <div className="row g-4">
                <Info
                  label="Total Liabilities"
                  value={cooperative?.financial?.share.totalLiabilities ?? "-"}
                />

                <Info
                  label="Total Assets"
                  value={cooperative?.financial?.share.totalAssets ?? "-"}
                />

                <Info
                  label="Liquid Assets (TLA)"
                  value={cooperative?.financial?.share.totalLiquidAssets ?? "-"}
                />

                <Info
                  label="TLA / TA Ratio"
                  value={cooperative?.financial?.share.tlaToTaRatio ?? "-"}
                />
              </div>
            </div>
          )}
          {financialsactiveTab === "capital" && (
            <div className="dashboard-card mt-3">
              <h6 className="text-uppercase mb-3">Capital Information</h6>

              <div className="row g-4">
                <Info
                  label="Share Capital"
                  value={cooperative?.financial?.loan?.totalShareCapital ?? "-"}
                />

                <Info
                  label="Reserve Fund"
                  value={cooperative?.financial?.loan?.totalReserveFund ?? "-"}
                />

                <Info
                  label="Core Capital"
                  value={cooperative?.financial?.loan?.coreCapital ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-4">Loan Information</h6>

              <div className="row g-4">
                <Info
                  label="Pass Loan"
                  value={cooperative?.financial?.loan?.totalPassLoan ?? "-"}
                />

                <Info
                  label="Sub Standard Loan"
                  value={
                    cooperative?.financial?.loan?.totalSubStandardLoan ?? "-"
                  }
                />

                <Info
                  label="Doubtful Loan"
                  value={cooperative?.financial?.loan?.totalDoubtfulLoan ?? "-"}
                />

                <Info
                  label="Loss Loan"
                  value={cooperative?.financial?.loan?.totalLossLoan ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Risk Indicators</h6>

              <div className="row g-4">
                <Info
                  label="NPL %"
                  value={cooperative?.financial?.loan?.nplPercentage ?? "-"}
                />

                <Info
                  label="Core Capital Ratio"
                  value={cooperative?.financial?.loan?.coreCapitalRatio ?? "-"}
                />

                <Info
                  label="Total Borrowing"
                  value={cooperative?.financial?.loan?.totalBorrowing ?? "-"}
                />
              </div>
            </div>
          )}
          {financialsactiveTab === "income" && (
            <div className="dashboard-card mt-3">
              <h6 className="text-uppercase mb-3">Income Information</h6>

              <div className="row g-4">
                <Info
                  label="Operational Income"
                  value={
                    cooperative?.financial?.income?.operationalIncome ?? "-"
                  }
                />

                <Info
                  label="Other Income"
                  value={cooperative?.financial?.income?.otherIncome ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Expense Information</h6>

              <div className="row g-4">
                <Info
                  label="Operational Expenses"
                  value={
                    cooperative?.financial?.income?.operationalExpenses ?? "-"
                  }
                />

                <Info
                  label="Other Expenses"
                  value={cooperative?.financial?.income?.otherExpenses ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Profit / Loss Information</h6>

              <div className="row g-4">
                <Info
                  label="Net Profit / Loss"
                  value={cooperative?.financial?.income?.netProfitLoss ?? "-"}
                />
              </div>
            </div>
          )}
          {financialsactiveTab === "audit" && (
            <div className="dashboard-card mt-3">
              <h6 className="text-uppercase mb-3">Auditor Information</h6>

              <div className="row g-4">
                <Info
                  label="Auditor Name"
                  value={cooperative?.financial?.audit?.auditorName ?? "-"}
                />

                <Info
                  label="Auditor Mobile"
                  value={cooperative?.financial?.audit?.auditorMobile ?? "-"}
                />

                <Info
                  label="Auditor Address"
                  value={cooperative?.financial?.audit?.auditorAddress ?? "-"}
                />

                <Info
                  label="ICAN Number"
                  value={cooperative?.financial?.audit?.icanNumber ?? "-"}
                />
              </div>

              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">AGM Information</h6>

              <div className="row g-4">
                <Info
                  label="AGM Date"
                  value={cooperative?.financial?.audit?.agmDate ?? "-"}
                />

                <Info
                  label="Participants"
                  value={cooperative?.financial?.audit?.participants ?? "-"}
                />

                <Info
                  label="Participants Percentage"
                  value={
                    cooperative?.financial?.audit?.participantsPercent ?? "-"
                  }
                />
              </div>
            </div>
          )}

          {financialsactiveTab === "tax" && (
            <div className="dashboard-card mt-3">
              <h6 className="text-uppercase mb-3">Tax Information</h6>

              <div className="row g-4">
                <Info
                  label="Income Tax"
                  value={cooperative?.financial?.tax?.incomeTax ?? "-"}
                />

                <Info
                  label="Interest Tax"
                  value={cooperative?.financial?.tax?.interestTax ?? "-"}
                />

                <Info
                  label="Employee Tax"
                  value={cooperative?.financial?.tax?.employeeTax ?? "-"}
                />

                <Info
                  label="Tax Others"
                  value={cooperative?.financial?.tax?.taxOthers ?? "-"}
                />
              </div>
              <hr className="my-3" />

              <h6 className="text-uppercase mb-3">Local Government</h6>

              <div className="row g-4">
                <Info
                  label="Service Tax"
                  value={cooperative?.financial?.tax?.serviceTax ?? "-"}
                />

                <Info
                  label="Rental Tax"
                  value={cooperative?.financial?.tax?.rentalTax ?? "-"}
                />

                <Info
                  label="Local Others"
                  value={cooperative?.financial?.tax?.localOthers ?? "-"}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <hr />
    </>
  );
};

export default FinancialInfo;
